import React, { useState, useEffect } from "react";
import "../../css/HomePage.css";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { toast } from "react-hot-toast";
import ImageCarousel from "../../components/ImageCarousel/ImageCarousel";
import { HeartFilled, HeartOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FavoriteIcon from "@mui/icons-material/Favorite";

const HomePage = () => {
    const [reviews, setReviews] = useState([]);
    const [currentReview, setCurrentReview] = useState(0);
    const [featured, setFeatured] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [carouselImages, setCarouselImages] = useState([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);

    const [auth] = useAuth();

    const apiUrl = `${process.env.REACT_APP_API}`;
    const navigate = useNavigate();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const response = await fetch(`${apiUrl}/reviews`);
                const data = await response.json();
                setReviews(data.data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchReviews();
    }, []);

    useEffect(() => {
        const loadCarouselImages = async () => {
            try {
                const { data } = await axios.get(`${apiUrl}/featured-product`);

                const images = data
                    .filter(product => product.images && product.images.front)
                    .slice(0, 10)
                    .map(product => ({
                        src: product.images.front,
                        alt: product.productName,
                        productId: product._id
                    }));

                setCarouselImages(images);
            } catch (err) {
                console.error("Error loading carousel images:", err);
            }
        };

        loadCarouselImages();
    }, []);

    useEffect(() => {
        if (reviews.length > 0) {
            const intervalId = setInterval(() => {
                setCurrentReview((prev) => (prev + 1) % reviews.length);
            }, 5000);
            return () => clearInterval(intervalId);
        }
    }, [reviews]);

    const filterProductByQuantity = (products) => {
        return products.filter((p) => {
            return p.productSize.sizes.some((size) => size.quantity > 0);
        });
    };

    useEffect(() => {
        const loadFeatured = async () => {
            try {
                setIsLoadingProducts(true);
                const { data } = await axios.get(`${apiUrl}/featured-product`);
                setFeatured(filterProductByQuantity(data));
                setIsLoadingProducts(false);
            } catch (err) {
                console.log(err);
                setIsLoadingProducts(false);
            }
        };

        loadFeatured();
    }, []);

    const fetchWishlist = async () => {
        try {
            const userId = auth.currentUser && auth.currentUser.id;
            if (userId) {
                const response = await axios.get(`${apiUrl}/wishlist`);
                setWishlist(response.data.products);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (auth.currentUser && auth.currentUser.id) {
            fetchWishlist();
        }
    }, [auth.currentUser]);

    const isInWishlist = (productId) => {
        return wishlist.some((item) => item._id === productId);
    };

    const addToWishlist = async (productId) => {
        try {
            const response = await axios.post(`${apiUrl}/wishlist/add`, {
                productId,
            });
            toast.success("Added to Wishlist");
            await fetchWishlist();
        } catch (error) {
            console.log(error);
            toast.error("Error adding to wishlist");
        }
    };

    const removeFromWishlist = async (productId) => {
        try {
            if (!auth) {
                return;
            }
            await axios.delete(`${apiUrl}/wishlist/delete/${productId}`);
            toast.success("Removed from Wishlist");
            await fetchWishlist();
        } catch (err) {
            console.log(err.message);
            toast.error("Error removing from wishlist");
        }
    };

    return (
        <>
            <div className="header-fixed">
                <Header />
            </div>

            {/* Carousel */}
            {carouselImages.length > 0 ? (
                <ImageCarousel images={carouselImages} />
            ) : (
                <div className="carousel-loading">
                    <div className="spinner-border" style={{ color: "#c70044" }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            {/* Trust / Features Ticker */}
            <div className="features-ticker">
                <div className="features-ticker-track">
                    {[...Array(3)].map((_, i) => (
                        <React.Fragment key={i}>
                            <div className="ticker-item">
                                <span className="ticker-icon">🚚</span>
                                <span className="ticker-title">Free Shipping</span>
                                <span className="ticker-desc">on orders over $50</span>
                            </div>
                            <span className="ticker-sep">✦</span>
                            <div className="ticker-item">
                                <span className="ticker-icon">🔄</span>
                                <span className="ticker-title">Free Exchanges</span>
                                <span className="ticker-desc">hassle-free size swaps</span>
                            </div>
                            <span className="ticker-sep">✦</span>
                            <div className="ticker-item">
                                <span className="ticker-icon">↩️</span>
                                <span className="ticker-title">Free Returns</span>
                                <span className="ticker-desc">no-questions asked</span>
                            </div>
                            <span className="ticker-sep">✦</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>
            {/* New Additions Section */}
            <div className="container mt-5 pt-4">
                <div className="text-center mb-5">
                    <h1 >NEW ARRIVALS</h1>
                    <div className="title-underline"></div>
                    <p className="section-subtitle">Discover our latest collection</p>
                </div>
            </div>

            {/* Products Grid */}
            <div className="container mb-5">
                {isLoadingProducts ? (
                    <div className="text-center my-5">
                        <div className="spinner-border" style={{ color: "#c70044" }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading products...</p>
                    </div>
                ) : (
                    <div className="products-grid">
                        {featured.length > 0 ? (
                            featured.map((product) => (
                                <div className="product-card-wrapper" key={product._id}>
                                    <div className="product-card">
                                        {/* Product Image */}
                                        <div className="product-image-container">
                                            <Link
                                                to={`/products/${product._id}`}
                                                onClick={() => {
                                                    window.scrollTo({ top: 0, behavior: "instant" });
                                                }}
                                            >
                                                <img
                                                    src={product.images.front}
                                                    alt={product.productName}
                                                    className="product-image"
                                                    loading="lazy"
                                                />
                                            </Link>

                                            {/* Sale Badge */}
                                            {product.salePrice > 0 && (
                                                <div className="sale-badge">
                                                    <LocalOfferIcon fontSize="small" className="me-1" />
                                                    {Math.round(
                                                        ((product.productPrice - product.salePrice) /
                                                            product.productPrice) *
                                                        100
                                                    )}
                                                    % OFF
                                                </div>
                                            )}

                                            {/* Wishlist Button */}
                                            <button
                                                className={`wishlist-btn ${
                                                    isInWishlist(product._id) ? "active" : ""
                                                }`}
                                                onClick={() => {
                                                    if (!localStorage.getItem("auth")) {
                                                        toast.error("Please log in to add to wishlist");
                                                        return;
                                                    }
                                                    if (isInWishlist(product._id)) {
                                                        removeFromWishlist(product._id);
                                                    } else {
                                                        addToWishlist(product._id);
                                                    }
                                                }}
                                            >
                                                {isInWishlist(product._id) ? (
                                                    <FavoriteIcon
                                                        style={{
                                                            fontSize: "1.3rem",
                                                            color: "#c70044"
                                                        }}
                                                    />
                                                ) : (
                                                    <FavoriteIcon
                                                        style={{
                                                            fontSize: "1.3rem",
                                                            color: "white"
                                                        }}
                                                    />
                                                )}
                                            </button>
                                        </div>

                                        {/* Product Info */}
                                        <div className="product-info">
                                            <Link
                                                to={`/products/${product._id}`}
                                                className="product-link"
                                            >
                                                <h5 className="product-name">{product.productName}</h5>
                                            </Link>

                                            <div className="product-pricing">
                                                {product.salePrice > 0 ? (
                                                    <>
                                                        <span className="sale-price">${product.salePrice}</span>
                                                        <span className="original-price">${product.productPrice}</span>
                                                    </>
                                                ) : (
                                                    <span className="current-price">${product.productPrice}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center my-5">
                                <p className="text-muted">No products available</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Reviews Section */}
            <div className="container mb-5 pb-4">
                <div className=" text-center mb-4">
                    <h1>CUSTOMER REVIEWS</h1>
                    <div className="title-underline"></div>
                    <p className="section-subtitle">See what our customers are saying</p>
                </div>

                {reviews.length > 0 ? (
                    <div className="review-carousel-container">
                        <div className="review-card">
                            <div className="review-stars mb-3">
                                {Array(reviews[currentReview].rating)
                                    .fill()
                                    .map((_, index) => (
                                        <StarIcon key={index} style={{ color: "#FECB38", fontSize: "1.8rem" }} />
                                    ))}
                            </div>
                            <Link to="/all-reviews" className="review-link">
                                <p className="review-text">
                                    "{reviews[currentReview].comment}"
                                </p>
                            </Link>
                            <p className="review-author">— {reviews[currentReview].name}</p>

                            {/* Review Dots */}
                            <div className="review-dots">
                                {reviews.map((_, index) => (
                                    <span
                                        key={index}
                                        className={`dot ${index === currentReview ? "active" : ""}`}
                                        onClick={() => setCurrentReview(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center">
                        <p className="text-muted">Loading reviews...</p>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default HomePage;